import { Trans, t } from "@lingui/macro";
import {
  Box,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { useCallback, useMemo } from "react";
import { useWorkoutProfilesContext } from "@/hooks/useWorkoutProfilesContext";
import { BlueSwitch, MenuList, MenuListItemVariant } from "@WahooFitness/redesignr";
import { ErrorBoundary, GlobalHeader } from "@WahooFitness/wahoo-offline-mfe";

const LEDSettings = ({ workoutProfileId }: { workoutProfileId: string }) => {
  const { palette } = useTheme();

  const {
    getWorkoutProfile,
    workoutProfilesLoading,
    workoutProfilesError,
    updateProfile,
    profileUpdateInProgress,
  } = useWorkoutProfilesContext();

  const workoutProfile = useMemo(
    () => getWorkoutProfile(workoutProfileId),
    [getWorkoutProfile, workoutProfileId]
  );

  const handleChangeLEDsMode = useCallback(
    (_: any, value: number) => {
      if (value !== null && value !== undefined && workoutProfile) {
        updateProfile(workoutProfile.external_id, "leds_mode", value);
      }
    },
    [updateProfile, workoutProfile]
  );

  const toggleDescription = useMemo(() => {
    switch (workoutProfile?.leds_mode) {
      case 0:
        return t`Metric-based LEDs are disabled`;
      case 1:
        return t`Shows your current speed compared to the ride average.`;
      case 5:
        return t`Shows your current power zone.`;
      case 6:
        return t`Shows your current heart rate zone.`;
      default:
        return t`Metric-based LEDs are disabled`;
    }
  }, [workoutProfile?.leds_mode]);

  const sections = useMemo(
    () => [
      {
        id: "leds_mode",
        content: t`LED metric indicator`,
        secondaryContent: (
          <Box display="flex" flexDirection="column" rowGap={1}>
            <ToggleButtonGroup
              exclusive
              fullWidth
              value={workoutProfile?.leds_mode ?? 0}
              disabled={profileUpdateInProgress}
              onChange={handleChangeLEDsMode}
            >
              <ToggleButton value={0}>
                <Trans>Off</Trans>
              </ToggleButton>
              <ToggleButton value={1}>
                <Trans>Speed</Trans>
              </ToggleButton>
              <ToggleButton value={5}>
                <Trans>Power</Trans>
              </ToggleButton>
              <ToggleButton value={6}>
                <Trans>HR</Trans>
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="prose-sm" color={palette.text.secondary}>
              {toggleDescription}
            </Typography>
          </Box>
        ),
        variant: MenuListItemVariant.Accordion,
        disabled: profileUpdateInProgress,
        expanded: true,
        actionComponent: <></>,
      },
      {
        id: "segments_commandeer_leds",
        content: t`Strava Live Segments`,
        secondaryContent: t`Indicates how far ahead or behind you are compared to the target time`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.segments_commandeer_leds}
            data-testid="segments_commandeer_leds"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "segments_commandeer_leds",
              !workoutProfile.segments_commandeer_leds
            );
          }
        },
      },
      {
        id: "plans_commandeer_leds",
        content: t`Planned workouts`,
        secondaryContent: t`Indicates how close you are to the chosen target metric`,
        variant: MenuListItemVariant.Action,
        disabled: profileUpdateInProgress,
        actionComponent: (
          <BlueSwitch
            disabled={profileUpdateInProgress}
            checked={workoutProfile?.plans_commandeer_leds}
            data-testid="plans_commandeer_leds"
          />
        ),
        action: () => {
          if (workoutProfile) {
            updateProfile(
              workoutProfile.external_id,
              "plans_commandeer_leds",
              !workoutProfile.plans_commandeer_leds
            );
          }
        },
      },
    ],
    [
      handleChangeLEDsMode,
      palette.text.secondary,
      profileUpdateInProgress,
      toggleDescription,
      updateProfile,
      workoutProfile,
    ]
  );

  return (
    <>
      <GlobalHeader title={t`LEDs`} />
      <Container maxWidth="sm" disableGutters sx={{ minHeight: "100%" }}>
        <ErrorBoundary isLoading={workoutProfilesLoading} error={workoutProfilesError}>
          <Box py={2}>
            <Typography
              sx={{ px: 2, pb: 1, width: "100%", display: "block" }}
              align="center"
              variant="prose-sm"
              color={palette.text.secondary}
            >
              <Trans>Settings only apply to ELEMNTs that have LEDs.</Trans>
            </Typography>
            <MenuList listItems={sections} />
          </Box>
        </ErrorBoundary>
      </Container>
    </>
  );
};

export default LEDSettings;
